import { useContext, useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import {
  TConfigParam,
  TDropdownOptions,
  TMenu,
  TPrivilege,
  TResponseModel,
  TRole,
} from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import PrivilegeMapper from "../../../components/privilege-mapper/index";
import {
  AddOrUpdateRoleInformation,
  GetConfigParamDataList,
  GetMenuHierarchyList,
  GetSpecificRoleInformation,
} from "../../../utils/api.constant";
import {
  CONFIG_GROUPS,
  ROLEUNIQUEID,
  STAKE_HOLDERS,
} from "../../../utils/constants";
import Utils from "../../../utils";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { UserContext } from "../../../context";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type DrawerProps = {
  id: number;
  isOpen?: any;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * This component is used to create/update roles and assign the privileges to the role.
 * This component is used in drawer and it takes the role id as a prop.
 * If the role id is present then it fetches the role details and populate the form.
 * If the role id is not present then it will render the form with empty fields.
 * The form has the fields like role name, role unique id, stake holder, description and privileges.
 * The privileges field is a custom component which is used to assign the privileges to the role.
 * This component is a form which is connected to the antd form component.
 * The form has the validation for the required fields.
 * The form has the submit button which will call the onFinish function when clicked.
 * The onFinish function will call the API to create or update the role and assign the privileges to the role.
 * The onFinish function will also call the onRefreshList function to refresh the list of roles.
 * The onFinish function will also close the drawer.
 */

export default function RoleForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [menuHierarchy, setMenuHierarchy] = useState<TMenu[]>([]);
  const [rolePrivileges, setRolePrivileges] = useState<TPrivilege[]>([]);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [stakeHolderOptions, setStakeHolderOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [selectedStakeHolder, setSelectedStakeHolder] = useState<string>();
  const context: any = useContext(UserContext);

  // Hook to handle on load operations
  useEffect(() => {
    const fetchData = async () => {
      await fetchSpecificRole();
      await getMenuHierarchyList();
      const stakeHolders = await fetchConfigParams(CONFIG_GROUPS.STAKEHOLDERS);
      setStakeHolderOptions(stakeHolders);
    };
    fetchData();
  }, []);

  // Function to fetch menu hierarchy list from the server
  const getMenuHierarchyList = async () => {
    try {
      const requestPayload = {
        data: {
          renderMenuRoleWise:
            context?.user?.roles[0]?.roleUniqueId === ROLEUNIQUEID.SUPERADMIN
              ? false
              : true,
        },
      };
      const response = await post(GetMenuHierarchyList, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        setMenuHierarchy(data);
      } else {
        console.error(`Error while fetching menu hierarchy: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // To get the list of parameter options
  const fetchConfigParams = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          groupUniqueId,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const updatedOptions = data.filter((option: TConfigParam) => {
            if (context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
              return option.paramUniqueId === "CUSTOMER";
            } else if (
              context?.user?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER
            ) {
              return option.paramUniqueId === "SERVICEPARTNER";
            } else if (context?.user?.stakeHolderName === STAKE_HOLDERS.IDEX) {
              return true; // Include all options for IDEX
            }
            return false; // Default case if none match
          });

          return Utils.mapDropdownOptions(updatedOptions);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const defaultValues: TRole = {
    id: 0,
    name: "",
    description: "",
    roleUniqueId: "",
    stakeHolderId: null,
    stakeHolderName: "",
  };

  // to get the list of checked privileges
  const handlePrivilegesChange = (checkedPrivileges: TPrivilege[]) => {
    setRolePrivileges(checkedPrivileges);
  };

  // Handle on submit event
  const onFinish = async (values: TRole) => {
    try {
      const payload = {
        data: {
          id: props?.id ? props?.id : 0,
          ...values,
          rolePrivileges: rolePrivileges,
        },
      };
      setIsLoading(true);

      const response = await post(AddOrUpdateRoleInformation, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        setRolePrivileges([]);
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific role details
  const fetchSpecificRole = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response: TResponseModel = await post(
          GetSpecificRoleInformation,
          payload
        );
        const { data, dataResponse } = response;
        const { description, returnCode } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const { rolePrivileges, ...restDetails } = data[0];
          setRolePrivileges(rolePrivileges);
          form.setFieldsValue(restDetails);
          setSelectedStakeHolder(data[0]?.stakeHolderName?.toLocaleUpperCase());
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Role`}
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="name"
                label="Role Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter role name"),
                  },
                ]}
              >
                <Input autoComplete="off" placeholder="Enter role name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="roleUniqueId"
                label="Role Unique Id"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter role unique id"),
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  disabled={props?.id ? true : false}
                  placeholder="Role unique id"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="stakeHolderId"
                label="Stake Holder"
                rules={[
                  { required: true, message: "Please select stake holder" },
                ]}
              >
                <Select
                  disabled={props?.id ? true : false}
                  placeholder="Select stakeholder"
                  options={stakeHolderOptions}
                  onChange={(value, option) =>
                    setSelectedStakeHolder((option as any)?.data?.paramUniqueId)
                  }
                ></Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="description" label="Description">
                <Input placeholder="Description" />
              </Form.Item>
            </Col>
          </Row>
          {form.getFieldValue("stakeHolderId") ? (
            <PrivilegeMapper
              selectedStakeHolder={selectedStakeHolder}
              menuHierarchy={menuHierarchy}
              preSelectedPrivileges={rolePrivileges}
              onPrivilegesChange={handlePrivilegesChange}
            ></PrivilegeMapper>
          ) : null}
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              className="btnDimensions"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
            >
              {props.id ? "Update" : "Add"}
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={props.onCloseDrawer}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
